/**
 * ActivityCommentSection - Seção expansível de comentários em atividade
 * Exibe lista de comentários e input para novo comentário
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */
import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageCircle, ChevronDown, ChevronUp, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ActivityCommentInput } from './ActivityCommentInput';
import { useActivityComments, formatMentionsForDisplay, ActivityComment } from '@/hooks/useActivityComments';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

// Props do componente
interface ActivityCommentSectionProps {
  activityId: string;
  commentsCount?: number;
  compact?: boolean;
  /** Se true, a seção inicia expandida por padrão */
  defaultExpanded?: boolean;
}

/**
 * Seção de comentários da atividade
 */
export function ActivityCommentSection({
  activityId,
  commentsCount = 0,
  compact = false,
  defaultExpanded = false
}: ActivityCommentSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const { user } = useAuth();
  
  // Hook de comentários (lazy load quando abrir)
  const {
    comments,
    isLoading,
    addComment,
    deleteComment,
    markMentionAsRead
  } = useActivityComments(isOpen ? activityId : null);

  // Handler para enviar comentário
  const handleSubmitComment = async (content: string, mentionedUserIds: string[]) => {
    await addComment.mutateAsync({ content, mentionedUserIds });
  };

  /**
   * Verifica se o usuário atual foi mencionado neste comentário e a menção está não lida
   */
  const getUserUnreadMention = (comment: ActivityComment) => {
    return comment.mentions?.find(
      m => m.mentioned_user_id === user?.id && !m.read_at
    );
  };

  // Contagem real (se já carregou) ou estimada
  const displayCount = isOpen ? comments.length : commentsCount;

  return (
    <TooltipProvider>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Header colapsável */}
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full justify-start gap-2 h-7 px-2 text-xs font-normal",
              "text-muted-foreground hover:text-foreground hover:bg-muted/50",
              displayCount > 0 && "text-primary"
            )}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            <span>
              {displayCount > 0 
                ? `${displayCount} comentário${displayCount !== 1 ? 's' : ''}`
                : 'Comentar'
              }
            </span>
            {isOpen ? (
              <ChevronUp className="h-3 w-3 ml-auto" />
            ) : (
              <ChevronDown className="h-3 w-3 ml-auto" />
            )}
          </Button>
        </CollapsibleTrigger>

        {/* Conteúdo expansível */}
        <CollapsibleContent className="mt-2 space-y-3">
          {/* Loading */}
          {isLoading && (
            <div className="space-y-2 pl-2">
              {[1, 2].map(i => (
                <div key={i} className="flex gap-2">
                  <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Lista de comentários */}
          {!isLoading && comments.length > 0 && (
            <div className="space-y-2 pl-2 border-l-2 border-muted ml-2">
              {comments.map((comment) => {
                const unreadMention = getUserUnreadMention(comment);
                
                return (
                  <div
                    key={comment.id}
                    className={cn(
                      "group flex gap-2 pl-2 py-1 rounded-r-md hover:bg-muted/30 transition-colors",
                      // Destaca se usuário foi mencionado e não leu ainda
                      unreadMention && "bg-amber-50 dark:bg-amber-950/20 border-l-2 border-amber-500"
                    )}
                  >
                    {/* Avatar do autor */}
                    <SmartAvatar
                      src={comment.author?.avatar_url}
                      name={comment.author?.full_name}
                      size="xs"
                      className="flex-shrink-0 mt-0.5"
                    />

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      {/* Header: Nome + Hora */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">
                          {comment.author?.full_name || 'Usuário'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {format(new Date(comment.created_at), "dd/MM HH:mm", { locale: ptBR })}
                        </span>
                      </div>

                      {/* Texto do comentário com menções formatadas */}
                      <p
                        className="text-xs text-muted-foreground mt-0.5 break-words"
                        dangerouslySetInnerHTML={{
                          __html: formatMentionsForDisplay(comment.content)
                        }}
                      />
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-1">
                      {/* Botão de marcar menção como lida (se usuário foi mencionado) */}
                      {unreadMention && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                              onClick={() => markMentionAsRead.mutate(unreadMention.id)}
                              disabled={markMentionAsRead.isPending}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Marcar como lida
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {/* Ação de deletar (apenas próprio comentário) */}
                      {comment.author_id === user?.id && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                              onClick={() => deleteComment.mutate(comment.id)}
                              disabled={deleteComment.isPending}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Excluir comentário
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Input para novo comentário */}
          <div className="pl-2">
            <ActivityCommentInput
              onSubmit={handleSubmitComment}
              isSubmitting={addComment.isPending}
              placeholder="Adicione um comentário... Use @ para mencionar"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </TooltipProvider>
  );
}
